import React from 'react';
import { Table, Tag, Progress, Button, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import {
  EyeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  StopOutlined,
  FlagOutlined,
} from '@ant-design/icons';
import type { Campaign } from '../../services/adsService';
import {
  formatPoints,
  formatDateRange,
  formatDateTime,
  statusLabel,
  typeLabel,
  STATUS_COLORS,
  TYPE_COLORS,
} from '../../utils/adsHelpers';

interface AdsTableProps {
  data: Campaign[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize?: number) => void;
  onRowClick: (record: Campaign) => void;
  onPause: (id: string, e: React.MouseEvent) => void;
  onResume: (id: string, e: React.MouseEvent) => void;
  onEnd: (id: string, e: React.MouseEvent) => void;
  onFlag: (id: string, e: React.MouseEvent) => void;
}

export const AdsTable: React.FC<AdsTableProps> = ({
  data,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
  onRowClick,
  onPause,
  onResume,
  onEnd,
  onFlag,
}) => {
  const columns: ColumnsType<Campaign> = [
    {
      title: 'Chiến dịch',
      key: 'campaign',
      width: 220,
      render: (_: unknown, record: Campaign) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.title}</div>
          <Tag
            color={TYPE_COLORS[record.type] || 'default'}
            style={{ marginTop: 4 }}
          >
            {typeLabel(record.type)}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Merchant',
      key: 'merchant',
      width: 160,
      render: (_: unknown, record: Campaign) => (
        <Link
          to={`/merchants/${record.merchantId}`}
          onClick={e => e.stopPropagation()}
        >
          <div>{record.merchantName || '–'}</div>
          <span style={{ fontSize: 12, color: '#888' }}>
            ID: {record.merchantId}
          </span>
        </Link>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => (
        <Tag
          color={
            STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default'
          }
        >
          {statusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'CPSO',
      dataIndex: 'costPerSuccessOrderPoints',
      key: 'cpso',
      width: 80,
      align: 'right' as const,
      render: (v: number) => formatPoints(v),
    },
    {
      title: 'Ngân sách',
      key: 'budget',
      width: 180,
      render: (_: unknown, record: Campaign) => {
        const totalVal = record.totalBudgetPoints || 0;
        const spent = record.spentPoints || 0;
        const pct = totalVal > 0 ? (spent / totalVal) * 100 : 0;
        const remaining = record.remainingPoints ?? totalVal - spent;
        const isNearLimit =
          remaining > 0 &&
          remaining <= (record.costPerSuccessOrderPoints || 0) * 3;
        const isOut = remaining <= 0;
        const status = isOut ? 'exception' : isNearLimit ? 'normal' : 'success';
        return (
          <div>
            <Progress
              percent={Math.min(pct, 100)}
              size="small"
              status={status as 'success' | 'exception' | 'normal'}
              showInfo={false}
            />
            <span style={{ fontSize: 12 }}>
              {formatPoints(spent)} / {formatPoints(totalVal)}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Clicks',
      key: 'clicks',
      width: 80,
      align: 'right' as const,
      render: (_: unknown, record: Campaign) => (
        <span>{record.clicksTotal ?? 0}</span>
      ),
    },
    {
      title: 'Đơn hàng',
      key: 'orders',
      width: 100,
      align: 'right' as const,
      render: (_: unknown, record: Campaign) => (
        <span>
          {record.attributedOrdersToday ?? 0}
          <span style={{ color: '#888', fontSize: 11 }}> / </span>
          {record.attributedOrdersTotal ?? 0}
        </span>
      ),
    },
    {
      title: 'Thời gian',
      key: 'time',
      width: 140,
      render: (_: unknown, record: Campaign) => (
        <span>{formatDateRange(record.startAt, record.endAt)}</span>
      ),
    },
    {
      title: 'Cập nhật',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 160,
      fixed: 'right' as const,
      render: (_: unknown, record: Campaign) => {
        const canResume =
          (record.status === 'PAUSED' || record.status === 'DRAFT') &&
          record.remainingPoints >= record.costPerSuccessOrderPoints;
        const resumeDisabled =
          (record.status === 'PAUSED' || record.status === 'DRAFT') &&
          !canResume;
        return (
          <Space size="small" onClick={e => e.stopPropagation()}>
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onRowClick(record)}
            >
              Xem
            </Button>
            {record.status === 'ACTIVE' && (
              <Button
                type="link"
                size="small"
                danger
                icon={<PauseCircleOutlined />}
                onClick={e => onPause(record.id, e)}
              >
                Tạm dừng
              </Button>
            )}
            {(record.status === 'PAUSED' || record.status === 'DRAFT') && (
              <Tooltip
                title={
                  resumeDisabled
                    ? 'Ngân sách còn lại không đủ (cần >= CPSO)'
                    : undefined
                }
              >
                <span>
                  <Button
                    type="link"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={e => onResume(record.id, e)}
                    disabled={resumeDisabled}
                  >
                    Tiếp tục
                  </Button>
                </span>
              </Tooltip>
            )}
            {['ACTIVE', 'PAUSED', 'DRAFT'].includes(record.status) && (
              <Button
                type="link"
                size="small"
                danger
                icon={<StopOutlined />}
                onClick={e => onEnd(record.id, e)}
              >
                Kết thúc
              </Button>
            )}
            <Button
              type="link"
              size="small"
              icon={<FlagOutlined />}
              style={record.adminNote ? { color: '#d97706' } : undefined}
              onClick={e => onFlag(record.id, e)}
            >
              {record.adminNote ? 'Đã gắn cờ' : 'Gắn cờ'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Table<Campaign>
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
      }}
      onChange={pagination => {
        const current = pagination?.current ?? page;
        const size = pagination?.pageSize ?? pageSize;
        onPageChange(current, size);
      }}
      onRow={record => ({
        onClick: () => onRowClick(record),
        style: { cursor: 'pointer' },
      })}
    />
  );
};
